/**
 * `Blueprint.source` — tagged-union schema for a blueprint's Clay data
 * source (D3, `clay-workflow-column-import` plan).
 *
 * Why: a blueprint can be wired to either a Clay Table (legacy) or a Clay
 * Workflow node (new). Both are persisted in the same nullable `source`
 * JSON column, keyed by `type`. This file mirrors the registry-map house
 * pattern in `handler-configs.schema.ts:80-121` — one schema per variant,
 * a registry map keyed by `type` for `terra/no-duplicate-schema-name`
 * alignment, and a discriminated union assembled from the map's values.
 *
 * Back-compat (D3): the legacy scalar `clayTableUrl` column is still
 * written/read alongside `source` for `clayTable`-typed blueprints — see
 * `deriveBlueprintSource` below, which implements the read-side
 * precedence rule (source wins; fall back to `clayTableUrl` when `source`
 * is absent or fails to parse). No backfill migration exists — legacy
 * rows only ever populate `clayTableUrl` and are derived at read time.
 *
 * Schema is the source of truth; the exported `BlueprintSource` type is
 * `z.infer` only (see `terra/no-parallel-schema-and-type`).
 *
 * `source` is a Prisma JSON column — always read through
 * `parseBlueprintSource` (which wraps `parseDbJson`), never
 * `BlueprintSourceSchema.parse()` directly. A single drifted row must
 * degrade to "no source" (with `clayTableUrl` fallback), not a 500.
 */

import { z } from 'zod';
import { parseDbJson, type DbJsonContext } from '../schemas/db-json';

// Per-variant schemas

/** Legacy Clay Table source — a single connected Clay table URL. */
export const ClayTableSourceSchema = z.object({
  type: z.literal('clayTable'),
  url: z.string(),
});

/**
 * Clay Workflow source — a single node within a Clay workflow whose
 * latest-run output is flattened into leaf-path "columns".
 *
 * `nodeName` is display-only (surfaced in connection banners/export copy)
 * and is optional because it isn't always known at write time.
 */
export const ClayWorkflowSourceSchema = z.object({
  type: z.literal('clayWorkflow'),
  url: z.string(),
  workspaceId: z.string(),
  workflowId: z.string(),
  nodeId: z.string(),
  nodeName: z.string().optional(),
});

// Registry map — schema per source type

/**
 * Map from source type -> schema. Keyed by type so
 * `terra/no-duplicate-schema-name` sees a single export name per schema.
 */
export const BLUEPRINT_SOURCE_SCHEMAS = {
  clayTable: ClayTableSourceSchema,
  clayWorkflow: ClayWorkflowSourceSchema,
} as const satisfies Record<'clayTable' | 'clayWorkflow', z.ZodType>;

// Discriminated union

export const BlueprintSourceSchema = z.discriminatedUnion('type', [
  ClayTableSourceSchema,
  ClayWorkflowSourceSchema,
]);

export type BlueprintSource = z.infer<typeof BlueprintSourceSchema>;

// Accessors

/**
 * Parse `Blueprint.source` (a nullable Prisma JSON column) into a
 * `BlueprintSource`, never throwing. `null`/`undefined` (no source
 * configured) and a malformed column value (wrong `type` literal, missing
 * required field) both degrade to `null` — callers fall back to
 * `clayTableUrl` via `deriveBlueprintSource`, they never see a crash.
 */
export function parseBlueprintSource(
  json: unknown,
  ctx: DbJsonContext = { column: 'blueprints.source' },
): BlueprintSource | null {
  return parseDbJson(BlueprintSourceSchema, json, ctx);
}

/**
 * Derive the effective `BlueprintSource` for a blueprint row, implementing
 * D3's read-side precedence: `source` wins when it parses; otherwise fall
 * back to the legacy `clayTableUrl` scalar (derived as a `clayTable`
 * source); otherwise `null` (no source configured).
 *
 * This is the single canonical read accessor — every consumer (API
 * responses, blueprint cache, skills tools, walker fixtures) should call
 * this rather than reading `source`/`clayTableUrl` directly.
 */
export function deriveBlueprintSource(row: {
  source: unknown;
  clayTableUrl: string | null | undefined;
}): BlueprintSource | null {
  const parsed = parseBlueprintSource(row.source);
  if (parsed) return parsed;
  if (row.clayTableUrl) return { type: 'clayTable', url: row.clayTableUrl };
  return null;
}
